import React from 'react';

export interface AvatarProps {
  src?: string | null;
  alt?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xlarge';
  className?: string;
  showStatus?: boolean;
  status?: 'online' | 'offline' | 'busy' | 'away';
  fallbackInitials?: string;
  beltColor?: string; // Added for belt color support
}

const Avatar: React.FC<AvatarProps> = ({
  src,
  alt = 'User avatar',
  size = 'md',
  className = '',
  showStatus = false,
  status = 'offline',
  fallbackInitials,
  beltColor,
}) => {
  // Size styles
  const sizeStyles = {
    xs: 'h-6 w-6 text-xs',
    sm: 'h-8 w-8 text-sm',
    md: 'h-10 w-10 text-base',
    lg: 'h-12 w-12 text-lg',
    xl: 'h-16 w-16 text-xl',
    xlarge: 'h-24 w-24 text-2xl'
  };
  
  // Status dot size based on avatar size
  const statusDotSizes = {
    xs: 'h-1.5 w-1.5',
    sm: 'h-2 w-2',
    md: 'h-2.5 w-2.5',
    lg: 'h-3 w-3',
    xl: 'h-4 w-4',
    xlarge: 'h-5 w-5'
  };
  
  // Status colors
  const statusColors = {
    online: 'bg-success',
    offline: 'bg-gray-400',
    busy: 'bg-error',
    away: 'bg-warning',
  };

  // Function to generate initials from a name
  const getInitials = () => {
    if (fallbackInitials) return fallbackInitials.substring(0, 2).toUpperCase();
    
    if (alt === 'User avatar') return 'U';
    
    return alt
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Custom border style for belt color
  const getBeltBorderStyle = () => {
    if (!beltColor) return {};
    return {
      boxShadow: `0 0 0 2px ${beltColor}`
    };
  };

  return (
    <div className={`relative ${className}`}>
      {src ? (
        <img
          src={src}
          alt={alt}
          className={`${sizeStyles[size]} object-cover rounded-full bg-transparent`}
          style={getBeltBorderStyle()}
        />
      ) : (
        <div
          className={`${sizeStyles[size]} rounded-full flex items-center justify-center bg-background-tertiary text-text-secondary dark:bg-background-secondary dark:text-text-primary font-medium`}
          role="img"
          aria-label={alt}
          style={getBeltBorderStyle()}
        >
          {getInitials()}
        </div>
      )}

      {showStatus && (
        <span
          className={`absolute bottom-0 right-0 block ${statusDotSizes[size]} rounded-full ring-2 ring-background-primary dark:ring-background-secondary ${statusColors[status]}`}
        />
      )}
    </div>
  );
};

export default Avatar;